import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, Select, message, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { ProjectRole, ProjectUser, ProjectUserStatus, hasAdminPrivileges } from '../../types/project';
import projectService from '../../services/projectService';
import InviteUserModal from './InviteUserModal';

interface ProjectUsersProps {
    projectId: number;
    currentUserRole: ProjectRole;
    onUpdate: () => void;
    projectName: string;
}

const ProjectUsers: React.FC<ProjectUsersProps> = ({ projectId, currentUserRole, onUpdate, projectName }) => {
    const { t } = useTranslation();
    const [users, setUsers] = useState<ProjectUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [inviteModalVisible, setInviteModalVisible] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, [projectId, currentUserRole]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const data = await projectService.getProjectUsers(projectId);
            setUsers(data);
        } catch (error) {
            console.error('Error fetching users:', error);
            message.error(t('project.users.fetchError'));
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = async (userId: number, newRole: ProjectRole) => {
        if (!projectId) return;

        try {
            const user = users.find(u => u.user_id === userId);
            if (!user) return;

            const currentRole = t(`roles.${user.role.toLowerCase()}`);
            const newRoleText = t(`roles.${newRole.toLowerCase()}`);

            Modal.confirm({
                title: t('users.confirmRoleChangeTitle'),
                content: (
                    <div style={{ padding: '16px 0' }}>
                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ fontWeight: 500, marginBottom: '8px' }}>{t('users.user')}:</div>
                            <div style={{ fontSize: '16px' }}>{user.user_first_name} {user.user_last_name}</div>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ fontWeight: 500, marginBottom: '8px' }}>{t('users.currentRole')}:</div>
                            <Tag color={
                                user.role === ProjectRole.ADMIN ? 'red' : 
                                user.role === ProjectRole.MEMBER ? 'blue' : 
                                'green'
                            }>
                                {currentRole}
                            </Tag>
                        </div>
                        <div>
                            <div style={{ fontWeight: 500, marginBottom: '8px' }}>{t('users.newRole')}:</div>
                            <Tag color={
                                newRole === ProjectRole.ADMIN ? 'red' : 
                                newRole === ProjectRole.MEMBER ? 'blue' : 
                                'green'
                            }>
                                {newRoleText}
                            </Tag>
                        </div>
                    </div>
                ),
                okText: t('common.yes'),
                cancelText: t('common.no'),
                onOk: async () => {
                    try {
                        const updatedUser = await projectService.updateUserRole(projectId, userId, newRole);
                        message.success(t('users.roleUpdated'));
                        await fetchUsers();
                    } catch (error: any) {
                        console.error('Error updating user role:', error);
                        message.error(error.message || t('users.roleUpdateError'));
                        // Reset the select value to the previous role
                        const select = document.querySelector(`#role-select-${userId}`) as HTMLSelectElement;
                        if (select) {
                            select.value = user.role;
                        }
                    }
                },
                onCancel: () => {
                    // Reset the select value to the previous role
                    const select = document.querySelector(`#role-select-${userId}`) as HTMLSelectElement;
                    if (select) {
                        select.value = user.role;
                    }
                },
                centered: true,
                width: 500,
                maskClosable: false,
                okButtonProps: {
                    style: { 
                        backgroundColor: '#1890ff',
                        borderColor: '#1890ff',
                        marginLeft: '8px'
                    }
                },
                cancelButtonProps: {
                    style: { 
                        marginLeft: '8px'
                    }
                },
                style: {
                    padding: '24px'
                },
                footer: (
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'flex-end',
                        marginTop: '24px',
                        padding: '0 24px 24px 24px'
                    }}>
                        <Button onClick={() => {
                            const select = document.querySelector(`#role-select-${userId}`) as HTMLSelectElement;
                            if (select) {
                                select.value = user.role;
                            }
                            Modal.destroyAll();
                        }}>
                            {t('common.no')}
                        </Button>
                        <Button 
                            type="primary" 
                            onClick={async () => {
                                try {
                                    const updatedUser = await projectService.updateUserRole(projectId, userId, newRole);
                                    message.success(t('users.roleUpdated'));
                                    await fetchUsers();
                                    Modal.destroyAll();
                                } catch (error: any) {
                                    console.error('Error updating user role:', error);
                                    message.error(error.message || t('users.roleUpdateError'));
                                    const select = document.querySelector(`#role-select-${userId}`) as HTMLSelectElement;
                                    if (select) {
                                        select.value = user.role;
                                    }
                                }
                            }}
                        >
                            {t('common.yes')}
                        </Button>
                    </div>
                )
            });
        } catch (error) {
            console.error('Error in handleRoleChange:', error);
            message.error(t('users.roleUpdateError'));
        }
    };

    const handleRemoveUser = async (userId: number) => {
        try {
            await projectService.removeUser(projectId, userId);
            message.success(t('project.users.userRemoved'));
            fetchUsers();
            onUpdate();
        } catch (error) {
            message.error(t('project.users.removeError'));
        }
    };

    const handleInviteSuccess = async () => {
        setInviteModalVisible(false);
        await fetchUsers();
        onUpdate();
    };

    const columns = [
        {
            title: t('project.users.name'),
            dataIndex: 'user_first_name',
            key: 'name',
            render: (text: string, record: ProjectUser) => 
                `${record.user_first_name} ${record.user_last_name}`
        },
        {
            title: t('project.users.email'),
            dataIndex: 'user_email',
            key: 'email'
        },
        {
            title: t('project.users.role'),
            dataIndex: 'role',
            key: 'role',
            render: (role: ProjectRole, record: ProjectUser) => {
                // Don't allow changing creator's role
                if (role === ProjectRole.CREATOR) {
                    return <Tag color="gold">{t('project.roles.creator')}</Tag>;
                }
                
                if (hasAdminPrivileges(currentUserRole)) {
                    return (
                        <Select
                            value={role}
                            onChange={(value) => handleRoleChange(record.user_id, value as ProjectRole)}
                            style={{ width: 120 }}
                        >
                            <Select.Option value={ProjectRole.ADMIN}>{t('project.roles.admin')}</Select.Option>
                            <Select.Option value={ProjectRole.MEMBER}>{t('project.roles.member')}</Select.Option>
                            <Select.Option value={ProjectRole.VIEWER}>{t('project.roles.viewer')}</Select.Option>
                        </Select>
                    );
                }
                return <Tag>{t(`project.roles.${role.toLowerCase()}`)}</Tag>;
            }
        },
        {
            title: t('project.users.status'),
            dataIndex: 'status',
            key: 'status',
            render: (status: ProjectUserStatus) => (
                <Tag color={status === ProjectUserStatus.active ? 'success' : 'warning'}>
                    {t(`project.status.${status}`)}
                </Tag>
            )
        },
        {
            title: t('common.actions'),
            key: 'actions',
            render: (text: string, record: ProjectUser) => {
                // Don't show remove button for creator
                if (record.role === ProjectRole.CREATOR) {
                    return null;
                }
                
                if (hasAdminPrivileges(currentUserRole)) {
                    return (
                        <Button 
                            type="link" 
                            danger 
                            onClick={() => handleRemoveUser(record.user_id)}
                        >
                            {t('common.remove')}
                        </Button>
                    );
                }
                return null;
            }
        }
    ];

    return (
        <div>
            {hasAdminPrivileges(currentUserRole) && (
                <div style={{ marginBottom: 16 }}>
                    <Button type="primary" onClick={() => setInviteModalVisible(true)}>
                        {t('project.users.inviteUser')}
                    </Button>
                </div>
            )}
            <Table
                columns={columns}
                dataSource={users}
                loading={loading}
                rowKey={(record) => `${record.project_id}-${record.user_id}`}
            />
            <InviteUserModal
                visible={inviteModalVisible}
                onCancel={() => setInviteModalVisible(false)}
                onSuccess={handleInviteSuccess}
                projectId={projectId}
            />
        </div>
    );
};

export default ProjectUsers; 